import { useCallback } from 'react'

import { createPersistedPreference } from '@/lib/hooks/createPersistedPreference'

/** Bounds the rail so it can never crowd out or disappear behind the page. */
export const SIDEBAR_MIN_WIDTH = 200
export const SIDEBAR_MAX_WIDTH = 420
export const SIDEBAR_DEFAULT_WIDTH = 256

export const clampSidebarWidth = (width: number) =>
  Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, Math.round(width)))

const widthPreference = createPersistedPreference<number>({
  key: 'titan.sidebar-width',
  fallback: SIDEBAR_DEFAULT_WIDTH,
  parse: (raw) => {
    const parsed = Number(raw)
    return Number.isFinite(parsed) ? clampSidebarWidth(parsed) : undefined
  },
})

/** The user's chosen width for the expanded sidebar rail, in pixels. */
export const useSidebarWidth = (): {
  width: number
  setWidth: (width: number) => void
  resetWidth: () => void
} => {
  const [width, setStoredWidth] = widthPreference.usePreference()

  const setWidth = useCallback(
    (next: number) => setStoredWidth(clampSidebarWidth(next)),
    [setStoredWidth]
  )

  const resetWidth = useCallback(
    () => setStoredWidth(SIDEBAR_DEFAULT_WIDTH),
    [setStoredWidth]
  )

  return { width, setWidth, resetWidth }
}
